import { useState, useRef, useEffect, useCallback } from 'react';

interface SessionConfig {
    input_audio_transcription: {
        model: string;
        language: string;
        prompt?: string;
    };
    turn_detection: {
        type: string;
        threshold?: number;
        prefix_padding_ms?: number;
        silence_duration_ms?: number;
        create_response?: boolean;
    };
    id?: string;
}

interface MessageEvent {
    type: string;
    session?: SessionConfig;
    transcript?: string;
    delta?: string;
}

class Session {
    apiKey: string;
    useSessionToken: boolean;
    mediaStream: MediaStream | null;
    peerConnection: RTCPeerConnection | null;
    dataChannel: RTCDataChannel | null;
    muted: boolean;
    onconnectionstatechange?: (state: string) => void;
    onmessage?: (parsed: MessageEvent) => void;
    ontrack?: (e: RTCTrackEvent) => void;
    onopen?: () => void;
    onerror?: (e: Error) => void;

    constructor(apiKey: string) {
        this.apiKey = apiKey;
        this.useSessionToken = true;
        this.mediaStream = null;
        this.peerConnection = null;
        this.dataChannel = null;
        this.muted = false;
    }

    async startTranscription(
        audioStream: MediaStream,
        sessionConfig: SessionConfig
    ): Promise<void> {
        await this.startInternal(
            audioStream,
            sessionConfig,
            '/v1/realtime/transcription_sessions'
        );
    }

    stop(): void {
        if (this.dataChannel) {
            this.dataChannel.close();
            this.dataChannel = null;
        }
        if (this.peerConnection) {
            this.peerConnection.close();
            this.peerConnection = null;
        }
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach((track) => track.stop());
            this.mediaStream = null;
        }
        this.muted = false;
    }

    async startInternal(
        audioStream: MediaStream,
        sessionConfig: SessionConfig,
        tokenEndpoint: string
    ): Promise<void> {
        this.mediaStream = audioStream;
        this.peerConnection = new RTCPeerConnection();
        this.peerConnection.ontrack = (event) => this.ontrack?.(event);
        this.peerConnection.addTrack(audioStream.getTracks()[0]);
        this.peerConnection.onconnectionstatechange = () => {
            if (this.peerConnection && this.onconnectionstatechange) {
                this.onconnectionstatechange(this.peerConnection.connectionState);
            }
        };
        this.dataChannel = this.peerConnection.createDataChannel('');
        this.dataChannel.onopen = () => this.onopen?.();
        this.dataChannel.onmessage = (event) => {
            if (this.onmessage) {
                try {
                    const parsedMessage = JSON.parse(event.data);
                    this.onmessage(parsedMessage);
                } catch (err) {
                    console.error('Error parsing message:', err);
                }
            }
        };
        const offer = await this.peerConnection.createOffer();
        await this.peerConnection.setLocalDescription(offer);
        try {
            const answer = await this.signal(offer, sessionConfig, tokenEndpoint);
            await this.peerConnection.setRemoteDescription(answer);
        } catch (error) {
            const errorObject =
                error instanceof Error ? error : new Error(String(error));
            this.onerror?.(errorObject);
        }
    }

    async signal(
        offer: RTCSessionDescriptionInit,
        sessionConfig: SessionConfig,
        tokenEndpoint: string
    ): Promise<RTCSessionDescriptionInit> {
        const apiBaseUrl = 'https://api.openai.com';
        const realtimeApiUrl = `${apiBaseUrl}/v1/realtime`;
        let serverResponse: Response;

        if (this.useSessionToken) {
            const sessionUrl = `${apiBaseUrl}${tokenEndpoint}`;
            const sessionResponse = await fetch(sessionUrl, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${this.apiKey}`,
                    'openai-beta': 'realtime-v1',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(sessionConfig),
            });
            if (!sessionResponse.ok) {
                throw new Error(
                    `Failed to request session token: ${sessionResponse.statusText}`
                );
            }
            const sessionData = await sessionResponse.json();
            const clientSecret = sessionData.client_secret.value;
            serverResponse = await fetch(`${realtimeApiUrl}`, {
                method: 'POST',
                body: offer.sdp,
                headers: {
                    Authorization: `Bearer ${clientSecret}`,
                    'Content-Type': 'application/sdp',
                },
            });
            if (!serverResponse.ok) {
                throw new Error(`Failed to signal: ${serverResponse.statusText}`);
            }
        } else {
            const formData = new FormData();
            formData.append('session', JSON.stringify(sessionConfig));
            if (offer.sdp) {
                formData.append('sdp', offer.sdp);
            }
            serverResponse = await fetch(`${realtimeApiUrl}`, {
                method: 'POST',
                body: formData,
                headers: { Authorization: `Bearer ${this.apiKey}` },
            });
            if (!serverResponse.ok) {
                throw new Error(`Failed to signal: ${serverResponse.statusText}`);
            }
        }

        const sdpResponseText = await serverResponse.text();
        return { type: 'answer', sdp: sdpResponseText };
    }
}

interface OpenAISTTState {
    isMicAvailable: boolean;
    isMicPermissionAllowed: boolean;
    isListening: boolean;
    isTranscribing: boolean;
    isConnecting: boolean;
    isErrorOccurred: boolean;
    errorMessage: string;
    partialTranscript: string;
    finalTranscript: string;
    startListening: () => Promise<void>;
    stopListening: () => void;
}

const useOpenAISTT = (apiKey: string, language: string = 'en', prompt?: string): OpenAISTTState => {
    const [isMicAvailable, setIsMicAvailable] = useState<boolean>(false);
    const [isMicPermissionAllowed, setIsMicPermissionAllowed] = useState<boolean>(false);
    const [isListening, setIsListening] = useState<boolean>(false);
    const [isTranscribing, setIsTranscribing] = useState<boolean>(false);
    const [isConnecting, setIsConnecting] = useState<boolean>(false);
    const [isErrorOccurred, setIsErrorOccurred] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [partialTranscript, setPartialTranscript] = useState<string>('');
    const [finalTranscript, setFinalTranscript] = useState<string>('');
    const sessionRef = useRef<Session | null>(null);
    const accumulatedTranscriptRef = useRef<string>('');
    const bufferedDeltasRef = useRef<string>('');
    const lastDeltaRef = useRef<string>('');

    useEffect(() => {
        const checkMicAvailability = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                setIsMicAvailable(true);
                setIsMicPermissionAllowed(true);
                stream.getTracks().forEach((track) => track.stop());
            } catch (error: any) {
                setIsMicAvailable(true); // Assume available but permission denied or other issue
                if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                    setIsMicPermissionAllowed(false);
                    setErrorMessage('Microphone permission denied.');
                    setIsErrorOccurred(true);
                } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                    setIsMicAvailable(false);
                    setErrorMessage('No microphone found.');
                    setIsErrorOccurred(true);
                } else {
                    setErrorMessage(`Error checking microphone: ${error.message}`);
                    setIsErrorOccurred(true);
                }
            }
        };

        checkMicAvailability();
    }, []);

    const handleMessage = useCallback((messageEvent: MessageEvent): void => {
        switch (messageEvent.type) {
            case 'transcription_session.created':
                setIsConnecting(false);
                break;

            case 'input_audio_buffer.speech_started':
                setIsTranscribing(true);
                break;

            case 'input_audio_buffer.speech_stopped':
                setIsTranscribing(false);
                break;

            case 'conversation.item.input_audio_transcription.delta':
                if (messageEvent.delta) {
                    lastDeltaRef.current = messageEvent.delta;
                    bufferedDeltasRef.current += messageEvent.delta;
                    const cleanedDelta = formatTranscriptDelta(bufferedDeltasRef.current);
                    setPartialTranscript(cleanedDelta);
                }
                break;

            case 'conversation.item.input_audio_transcription.completed':
                if (messageEvent.transcript) {
                    const formattedTranscript = formatCompletedTranscript(
                        accumulatedTranscriptRef.current,
                        messageEvent.transcript
                    );
                    accumulatedTranscriptRef.current += formattedTranscript;
                    setFinalTranscript(accumulatedTranscriptRef.current);
                    setPartialTranscript('');
                    bufferedDeltasRef.current = '';
                }
                break;

            default:
                console.warn('Unhandled message type:', messageEvent.type);
                break;
        }
    }, []);

    const formatTranscriptDelta = (delta: string): string => {
        let formattedDelta = delta.trim();
        formattedDelta = formattedDelta.replace(/([.!?])\s*([a-zA-Z])/g, '$1 $2');
        return formattedDelta;
    };

    const formatCompletedTranscript = (
        previousTranscript: string,
        newTranscript: string
    ): string => {
        let formattedTranscript = newTranscript.trim();
        if (previousTranscript.match(/[.!?]\s*$/)) {
            formattedTranscript =
                formattedTranscript.charAt(0).toUpperCase() +
                formattedTranscript.slice(1);
        } else if (
            previousTranscript.length > 0 &&
            !previousTranscript.endsWith(' ')
        ) {
            formattedTranscript =
                ' ' +
                formattedTranscript.charAt(0).toLowerCase() +
                formattedTranscript.slice(1);
        }
        formattedTranscript = formattedTranscript.replace(/\s+([,.!?:;])/g, '$1');
        formattedTranscript = formattedTranscript.replace(
            /([.!?])\s*([a-zA-Z])/g,
            '$1 $2'
        );
        return formattedTranscript;
    };

    const handleError = useCallback((error: Error): void => {
        console.error(error);
        setErrorMessage(error.message);
        setIsErrorOccurred(true);
        setIsListening(false);
        setIsConnecting(false);
        setIsTranscribing(false);
        if (sessionRef.current) {
            sessionRef.current.stop();
            sessionRef.current = null;
        }
    }, []);

    const startListening = useCallback(async (): Promise<void> => {
        if (!apiKey) {
            setErrorMessage('Missing OpenAI API Key.');
            setIsErrorOccurred(true);
            return;
        }

        if (!isMicAvailable) {
            setErrorMessage('No microphone available.');
            setIsErrorOccurred(true);
            return;
        }

        if (!isMicPermissionAllowed) {
            setErrorMessage('Microphone permission not granted.');
            setIsErrorOccurred(true);
            return;
        }

        setIsListening(true);
        setIsConnecting(true);
        setIsErrorOccurred(false);
        setErrorMessage('');
        setPartialTranscript('');
        bufferedDeltasRef.current = '';

        sessionRef.current = new Session(apiKey);
        sessionRef.current.onconnectionstatechange = (state) => {
            if (state === 'disconnected' || state === 'failed') {
                setIsListening(false);
                setIsConnecting(false);
                setIsTranscribing(false);
                setErrorMessage(`Connection ${state}. Please try again.`);
                setIsErrorOccurred(true);
                sessionRef.current = null;
            }
        };
        sessionRef.current.onmessage = handleMessage;
        sessionRef.current.onerror = handleError;

        const sessionConfig: SessionConfig = {
            input_audio_transcription: {
                model: 'gpt-4o-transcribe',
                language: language,
                prompt: prompt,
            },
            turn_detection: {
                type: 'server_vad',
            },
        };

        try {
            const microphoneStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            await sessionRef.current.startTranscription(
                microphoneStream,
                sessionConfig
            );
        } catch (error: any) {
            handleError(error);
        }
    }, [apiKey, language, prompt, handleMessage, handleError, isMicAvailable, isMicPermissionAllowed]);

    const stopListening = useCallback((): void => {
        if (sessionRef.current) {
            sessionRef.current.stop();
            sessionRef.current = null;
        }
        setIsListening(false);
        setIsConnecting(false);
        setIsTranscribing(false);
        if (partialTranscript.trim()) {
            const finalText = formatCompletedTranscript(
                accumulatedTranscriptRef.current,
                partialTranscript
            );
            accumulatedTranscriptRef.current += finalText;
            setFinalTranscript(accumulatedTranscriptRef.current);
            setPartialTranscript('');
        }
    }, [partialTranscript, accumulatedTranscriptRef, formatCompletedTranscript]);

    useEffect(() => {
        return () => {
            if (sessionRef.current) {
                sessionRef.current.stop();
                sessionRef.current = null;
            }
        };
    }, []);

    return {
        isMicAvailable,
        isMicPermissionAllowed,
        isListening,
        isTranscribing,
        isConnecting,
        isErrorOccurred,
        errorMessage,
        partialTranscript,
        finalTranscript,
        startListening,
        stopListening,
    };
};

export default useOpenAISTT;